/**
 * Shared validation constants for use across backend DTOs and frontend Zod schemas.
 * These provide consistent limits and messages for common field types.
 */
// ============================================================================
// NAME FIELDS
// ============================================================================
/**
 * Standard name field constraints (e.g., category names, domain names, use names)
 */
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 60;
export const NAME_VALIDATION_MESSAGES = {
    max: `Name must be at most ${NAME_MAX_LENGTH} characters`,
    min: `Name must be at least ${NAME_MIN_LENGTH} characters`,
    required: 'Name is required',
};
// ============================================================================
// DESCRIPTION FIELDS
// ============================================================================
/**
 * Short description constraints (e.g., uses, domains, categories)
 */
export const SHORT_DESCRIPTION_MAX_LENGTH = 500;
export const SHORT_DESCRIPTION_VALIDATION_MESSAGES = {
    max: `Description must be at most ${SHORT_DESCRIPTION_MAX_LENGTH} characters`,
};
/**
 * Long description constraints (e.g., technologies, companies, assessments)
 */
export const LONG_DESCRIPTION_MAX_LENGTH = 2000;
export const LONG_DESCRIPTION_VALIDATION_MESSAGES = {
    max: `Description must be at most ${LONG_DESCRIPTION_MAX_LENGTH} characters`,
};
// ============================================================================
// TITLE FIELDS
// ============================================================================
/**
 * Title field constraints (e.g., challenge titles, survey titles)
 */
export const TITLE_MIN_LENGTH = 2;
export const TITLE_MAX_LENGTH = 60;
export const TITLE_VALIDATION_MESSAGES = {
    max: `Title must be at most ${TITLE_MAX_LENGTH} characters`,
    min: `Title must be at least ${TITLE_MIN_LENGTH} characters`,
    required: 'Title is required',
};
// ============================================================================
// LLM CHAT FIELDS
// ============================================================================
/**
 * Bounds for inbound chat payloads forwarded to the LLM and persisted to logs.
 * The request boundary is the only guard (the global pipe runs whitelist:false),
 * so these cap token cost, memory, and llm_logs growth.
 */
export const CHAT_MESSAGES_MAX_SIZE = 100;
export const CHAT_MESSAGE_PARTS_MAX_SIZE = 50;
export const CHAT_MESSAGE_PART_TEXT_MAX_LENGTH = 10_000;
export const CHAT_MESSAGE_VALIDATION_MESSAGES = {
    messagesMax: `Please provide up to ${CHAT_MESSAGES_MAX_SIZE} chat messages.`,
    partsMax: `Please provide up to ${CHAT_MESSAGE_PARTS_MAX_SIZE} parts per message.`,
    textMax: `Message text must be at most ${CHAT_MESSAGE_PART_TEXT_MAX_LENGTH} characters.`,
};
// ============================================================================
// MISSION ASSESSMENT FULL-ASSESSMENT SNAPSHOT FIELDS
// ============================================================================
/**
 * Bounds for the Full Assessment Generation form snapshot — the current form
 * state that rides every generate exchange as LLM context. The editable fields
 * are uncapped, so the web side clamps over-long entries to these caps before
 * sending and the API enforces them at the request boundary. Single-sourced
 * here so the clamp and the validator can never drift.
 */
export const MA_FULL_SNAPSHOT_INTER_NAME_MAX_LENGTH = 500;
export const MA_FULL_SNAPSHOT_SERVICE_UNIT_MAX_LENGTH = 200;
export const MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH = 4000;
export const MA_FULL_SNAPSHOT_VALIDATION_MESSAGES = {
    expertNote: `Expert note content must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
    generalNotes: `General notes must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
    interName: `Counterpart system name must be ${MA_FULL_SNAPSHOT_INTER_NAME_MAX_LENGTH} characters or fewer.`,
    nextMrl: `Next MRL notes must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
    nextTrl: `Next TRL notes must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
    serviceUnit: `Unit must be ${MA_FULL_SNAPSHOT_SERVICE_UNIT_MAX_LENGTH} characters or fewer.`,
    supplyChain: `Supply chain notes must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
    taskRecommendations: `Task recommendations must be ${MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH} characters or fewer.`,
};
